import React, { useState, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProfilePictureTab = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file?.size > 2 * 1024 * 1024) {
      setError('Image size must be less than 2MB');
      return;
    }

    setError('');
    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader?.result);
    };
    reader?.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    setIsUploading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Create unique filename
      const fileExt = selectedFile?.name?.split('.')?.pop();
      const fileName = `${user?.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // Note: Storage bucket needs to be created in Supabase Dashboard
      // For now, we'll update the avatar_url field with a placeholder
      // In production, you would upload to Supabase Storage first
      
      const updates = {
        avatar_url: filePath,
      };

      const { error: updateError } = await updateProfile(updates);

      if (updateError) {
        setError(updateError?.message || 'Failed to update profile picture');
      } else {
        setSuccessMessage('Profile picture updated successfully!');
        setSelectedFile(null);
        setPreviewUrl(null);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!user) return;

    setIsUploading(true);
    setError('');
    setSuccessMessage('');

    try {
      const updates = {
        avatar_url: null,
      };

      const { error: updateError } = await updateProfile(updates);

      if (updateError) {
        setError(updateError?.message || 'Failed to remove profile picture');
      } else {
        setSuccessMessage('Profile picture removed successfully!');
        setPreviewUrl(null);
        setSelectedFile(null);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError('');
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-success/10 border border-success/20">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-success">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-error/10 border border-error/20">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Current Profile Picture */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Current Profile Picture</h3>
        <div className="flex items-center gap-6">
          <div className="w-32 h-32 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-4xl font-semibold border-4 border-border">
            {userProfile?.full_name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-3">
              {userProfile?.avatar_url ? 'You have a profile picture set' : 'No profile picture set'}
            </p>
            {userProfile?.avatar_url && (
              <Button
                onClick={handleRemove}
                variant="destructive"
                size="sm"
                iconName="Trash2"
                disabled={isUploading}
              >
                Remove Picture
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Upload New Picture */}
      <div className="pt-6 border-t border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Upload New Picture</h3>
        
        {/* Preview */}
        {previewUrl && (
          <div className="mb-6">
            <p className="text-sm font-medium text-foreground mb-3">Preview:</p>
            <div className="flex items-center gap-6">
              <img
                src={previewUrl}
                alt="Preview"
                className="w-32 h-32 rounded-full object-cover border-4 border-border"
              />
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  {selectedFile?.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {(selectedFile?.size / 1024)?.toFixed(2)} KB
                </p>
              </div>
            </div>
          </div>
        )}

        {/* File Input */}
        <div className="mb-6">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="profile-picture-input"
          />
          <label
            htmlFor="profile-picture-input"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-muted text-foreground hover:bg-muted/80 transition-colors duration-250 cursor-pointer border border-border"
          >
            <Icon name="Upload" size={18} />
            Choose Image
          </label>
          <p className="text-xs text-muted-foreground mt-2">
            Supported formats: JPG, PNG, WEBP. Max size: 2MB
          </p>
        </div>

        {/* Action Buttons */}
        {selectedFile && (
          <div className="flex items-center gap-3">
            <Button
              onClick={handleUpload}
              variant="default"
              loading={isUploading}
              iconName="Upload"
            >
              Upload Picture
            </Button>
            <Button
              onClick={handleCancel}
              variant="outline"
              disabled={isUploading}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-muted/50 rounded-lg p-4 border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Profile Picture Guidelines</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Use a clear, professional photo</li>
              <li>• Image should be square for best results</li>
              <li>• Recommended minimum size: 400x400 pixels</li>
              <li>• Your face should be clearly visible</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureTab;